import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Image, Badge, Pagination, Form, Row, Col, Modal, Spinner, Container, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ApprovalRequestsTable = () => {
    const [offers, setOffers] = useState([]);
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItemType, setSelectedItemType] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [comment, setComment] = useState('');
    const [approvingItemId, setApprovingItemId] = useState(null);
    const [activeTab, setActiveTab] = useState('offers');
    const navigate = useNavigate();

    useEffect(() => {
        fetchOffers();
        fetchNews();
    }, []);

    const fetchOffers = async () => {
        try {
            const response = await axios.get('https://api.admin-panel.nhcholding.com/api/offers', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.data && Array.isArray(response.data.data)) {
                setOffers(response.data.data);
            } else {
                toast.error('Failed to load offers.');
            }
        } catch (error) {
            toast.error('Error loading offers.');
        }
    };

    const fetchNews = async () => {
        try {
            const response = await axios.get('https://api.admin-panel.nhcholding.com/api/news', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.data && Array.isArray(response.data.data)) {
                setNews(response.data.data);
            } else {
                toast.error('Failed to load news.');
            }
        } catch (error) {
            toast.error('Error loading news.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusMapping = {
            approved: { text: 'Approved & Published', variant: 'success' },
            rejected_for_edit: { text: 'Needs Revision', variant: 'warning' },
            rejected: { text: 'Rejected', variant: 'danger' },
            manager_approved: { text: 'Approved by Manager', variant: 'info' },
            pending: { text: 'In Review', variant: 'secondary' }
        };
        const { text, variant } = statusMapping[status] || { text: status, variant: 'dark' };
        return <Badge bg={variant}>{text}</Badge>;
    };

    const isExpired = (endDate) => {
        const today = new Date();
        const offerEndDate = new Date(endDate);
        return offerEndDate < today;
    };

    const handleApprove = async (itemId, itemType) => {
        setApprovingItemId(itemId);
        try {
            const endpoint = itemType === 'offer' ? 
                `https://api.admin-panel.nhcholding.com/api/offers/${itemId}/approve` :
                `https://api.admin-panel.nhcholding.com/api/news/${itemId}/approve`;
                
            const response = await axios.post(endpoint, null, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            // Handle different response messages with proper validation
            const responseMessage = response.data.message;
            
            // Check for "already approved by manager" message
            if (responseMessage && responseMessage.includes('already approved by manager')) {
                toast.error(`This ${itemType} is already approved by manager and cannot be approved again.`);
                return;
            }
            
            // Check for "already approved" message
            if (responseMessage && responseMessage.includes('already approved')) {
                toast.error(`This ${itemType} is already approved and published.`);
                return;
            }

            // Success case
            toast.success(`${itemType === 'offer' ? 'Offer' : 'News'} has been approved successfully!`);
            
            if (itemType === 'offer') {
                updateOfferStatus(itemId, 'approved');
            } else {
                updateNewsStatus(itemId, 'approved');
            }
        } catch (error) {
            // Handle API errors
            if (error.response && error.response.data && error.response.data.message) {
                const errorMessage = error.response.data.message;
                
                // Check for specific error messages in the error response
                if (errorMessage.includes('already approved by manager')) {
                    toast.error(`This ${itemType} is already approved by manager and cannot be approved again.`);
                } else if (errorMessage.includes('already approved')) {
                    toast.error(`This ${itemType} is already approved and published.`);
                } else {
                    toast.error(errorMessage);
                }
            } else {
                toast.error(`Failed to approve ${itemType}. Please try again.`);
            }
        } finally {
            setApprovingItemId(null);
        }
    };

    const handleReject = async () => {
        if (comment.length < 10) {
            toast.error("Rejection comment must be at least 10 characters.");
            return;
        }
        try {
            const endpoint = selectedItemType === 'offer' ? 
                `https://api.admin-panel.nhcholding.com/api/offers/${selectedItem}/reject` :
                `https://api.admin-panel.nhcholding.com/api/news/${selectedItem}/reject`;
                
            await axios.post(endpoint, {
                comment,
                rejection_type: rejectionReason
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            
            toast.success(`${selectedItemType === 'offer' ? 'Offer' : 'News'} has been rejected`);
            
            if (selectedItemType === 'offer') {
                updateOfferStatus(selectedItem, rejectionReason);
            } else {
                updateNewsStatus(selectedItem, rejectionReason);
            }
            
            closeRejectModal();
        } catch (error) {
            toast.error(`Failed to reject ${selectedItemType}`);
        }
    };

    const updateOfferStatus = (offerId, newStatus) => {
        setOffers((prevOffers) =>
            prevOffers.map((offer) =>
                offer.id === offerId ? { ...offer, status: newStatus } : offer
            )
        );
    };

    const updateNewsStatus = (newsId, newStatus) => {
        setNews((prevNews) =>
            prevNews.map((newsItem) =>
                newsItem.id === newsId ? { ...newsItem, status: newStatus } : newsItem
            )
        );
    };

    const openRejectModal = (itemId, itemType) => {
        setSelectedItem(itemId);
        setSelectedItemType(itemType);
        setShowRejectModal(true);
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setRejectionReason('');
        setComment('');
    };

    const handleViewOffer = (id) => {
        navigate(`/offers/view/${id}`);
    };

    const handleViewNews = (id) => {
        navigate(`/cms/blog-management/view/${id}`);
    };

    const filterData = (data, isOffer = true) => {
        return data.filter((item) => {
            const nameEn = isOffer ? item.offer_name_en : item.title_en;
            const nameAr = isOffer ? item.offer_name_ar : item.title_ar;
            const matchesSearch = nameEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
                nameAr.toLowerCase().includes(searchTerm.toLowerCase());
            
            let matchesStartDate = true;
            let matchesEndDate = true;
            
            if (isOffer) {
                matchesStartDate = startDateFilter ? item.offer_start_date === startDateFilter : true;
                matchesEndDate = endDateFilter ? item.offer_end_date === endDateFilter : true;
            } else {
                // For news, we can filter by created date
                const createdDate = new Date(item.created_at).toISOString().split('T')[0];
                matchesStartDate = startDateFilter ? createdDate >= startDateFilter : true;
                matchesEndDate = endDateFilter ? createdDate <= endDateFilter : true;
            }
            
            const matchesStatus = statusFilter ? item.status === statusFilter : true;

            return matchesSearch && matchesStartDate && matchesEndDate && matchesStatus;
        });
    };

    const currentData = activeTab === 'offers' ? offers : news;
    const filteredData = filterData(currentData, activeTab === 'offers');
    
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const renderOffersTable = () => (
        <Table responsive striped bordered hover className="text-center">
            <thead>
                <tr>
                    <th>Sr. No</th>
                    <th>Offer Id</th>
                    <th>Offer Banner</th>
                    <th>Offer Name (En)</th>
                    <th>Offer Name (Ar)</th>
                    <th>Offer Start Date</th>
                    <th>Offer End Date</th>
                    <th>Approval Status</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {currentItems.length === 0 ? (
                    <tr>
                        <td colSpan="9" className="text-center">No offers are available</td>
                    </tr>
                ) : (
                    currentItems.map((offer, index) => {
                        const expired = isExpired(offer.offer_end_date);
                        return (
                            <tr key={offer.id}>
                                <td>{indexOfFirstItem + index + 1}</td>
                                <td>{offer.id}</td>
                                <td>
                                    {offer.offer_banner && (
                                        <Image
                                            src={`https://api.admin-panel.nhcholding.com/storage/${offer.offer_banner}`}
                                            alt="Offer Banner"
                                            width={80}
                                            height={80}
                                            rounded
                                        />
                                    )}
                                </td>
                                <td>{offer.offer_name_en}</td>
                                <td>{offer.offer_name_ar}</td>
                                <td>{offer.offer_start_date}</td>
                                <td>{offer.offer_end_date}</td>
                                <td>{getStatusBadge(offer.status)}</td>
                                <td>
                                    <Button variant="info" size="sm" onClick={() => handleViewOffer(offer.id)}>View</Button>{' '}
                                    {!expired && (
                                        <>
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={() => handleApprove(offer.id, 'offer')}
                                                disabled={offer.status === 'approved' || approvingItemId === offer.id}
                                            >
                                                {approvingItemId === offer.id ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : "Approve"}
                                            </Button>{' '}
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => openRejectModal(offer.id, 'offer')}
                                                disabled={offer.status === 'approved'}
                                            >
                                                Reject
                                            </Button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        );
                    })
                )}
            </tbody>
        </Table>
    );

    const renderNewsTable = () => (
        <Table responsive striped bordered hover className="text-center">
            <thead>
                <tr>
                    <th>Sr. No</th>
                    <th>News Id</th>
                    <th>Featured Image</th>
                    <th>News Title (En)</th>
                    <th>News Title (Ar)</th>
                    <th>Created By</th>
                    <th>Created Date</th>
                    <th>Approval Status</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {currentItems.length === 0 ? (
                    <tr>
                        <td colSpan="9" className="text-center">No news are available</td>
                    </tr>
                ) : (
                    currentItems.map((newsItem, index) => (
                        <tr key={newsItem.id}>
                            <td>{indexOfFirstItem + index + 1}</td>
                            <td>{newsItem.id}</td>
                            <td>
                                {newsItem.featured_image && (
                                    <Image
                                        src={`https://api.admin-panel.nhcholding.com/storage/${newsItem.featured_image}`}
                                        alt="Featured Image"
                                        width={80}
                                        height={80}
                                        rounded
                                    />
                                )}
                            </td>
                            <td>{newsItem.title_en}</td>
                            <td>{newsItem.title_ar}</td>
                            <td>{newsItem.creator?.name || 'Unknown'}</td>
                            <td>{new Date(newsItem.created_at).toLocaleDateString()}</td>
                            <td>{getStatusBadge(newsItem.status)}</td>
                            <td>
                                <Button variant="info" size="sm" onClick={() => handleViewNews(newsItem.id)}>View</Button>{' '}
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleApprove(newsItem.id, 'news')}
                                    disabled={newsItem.status === 'approved' || approvingItemId === newsItem.id}
                                >
                                    {approvingItemId === newsItem.id ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : "Approve"}
                                </Button>{' '}
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => openRejectModal(newsItem.id, 'news')}
                                    disabled={newsItem.status === 'approved'}
                                >
                                    Reject
                                </Button>
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </Table>
    );

    if (loading) return <div>Loading approval requests...</div>;

    return (
        <Container fluid>
            <div className="table-container">
                <ToastContainer />
                <div className='approval-requests-page mt-5 mb-5'>
                    <h3>Approval Requests</h3>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => {
                        setActiveTab(k);
                        setCurrentPage(1);
                        setSearchTerm('');
                        setStartDateFilter('');
                        setEndDateFilter('');
                        setStatusFilter('');
                    }}
                    className="mb-3"
                >
                    <Tab eventKey="offers" title="Offers">
                        <Form className="mb-3">
                            <Row>
                                <Col md={4}>
                                    <Form.Control
                                        type="search"
                                        placeholder="Search by Offer Name"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="input-brand-shadow"
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Control
                                        as="select"
                                        value={startDateFilter}
                                        onChange={(e) => setStartDateFilter(e.target.value)}
                                        className="input-brand-shadow"
                                    >
                                        <option value="">Start Date</option>
                                        {[...new Set(offers.map(offer => offer.offer_start_date))].map((date) => (
                                            <option key={date} value={date}>{date}</option>
                                        ))}
                                    </Form.Control>
                                </Col>
                                <Col md={2}>
                                    <Form.Control
                                        as="select"
                                        value={endDateFilter}
                                        onChange={(e) => setEndDateFilter(e.target.value)}
                                        className="input-brand-shadow"
                                    >
                                        <option value="">End Date</option>
                                        {[...new Set(offers.map(offer => offer.offer_end_date))].map((date) => (
                                            <option key={date} value={date}>{date}</option>
                                        ))}
                                    </Form.Control>
                                </Col>
                                <Col md={2}>
                                    <Form.Control
                                        as="select"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="input-brand-shadow"
                                    >
                                        <option value="">Status</option>
                                        <option value="approved">Approved & Published</option>
                                        <option value="rejected_for_edit">Needs Revision</option>
                                        <option value="rejected">Rejected</option>
                                        <option value="manager_approved">Approved by Manager</option>
                                        <option value="pending">In Review</option>
                                    </Form.Control>
                                </Col>
                                <Col md={2} className="text-end">
                                    <Button variant="secondary" onClick={() => {
                                        setSearchTerm('');
                                        setStartDateFilter('');
                                        setEndDateFilter('');
                                        setStatusFilter('');
                                    }}>Clear Filters</Button>
                                </Col>
                            </Row>
                        </Form>

                        <div className="table-brand-shadow">
                            {renderOffersTable()}
                        </div>
                    </Tab>

                    <Tab eventKey="news" title="News">
                        <Form className="mb-3">
                            <Row>
                                <Col md={6}>
                                    <Form.Control
                                        type="search"
                                        placeholder="Search by News Title"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="input-brand-shadow"
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Control
                                        as="select"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="input-brand-shadow"
                                    >
                                        <option value="">Status</option>
                                        <option value="approved">Approved & Published</option>
                                        <option value="rejected_for_edit">Needs Revision</option>
                                        <option value="rejected">Rejected</option>
                                        <option value="manager_approved">Approved by Manager</option>
                                        <option value="pending">In Review</option>
                                    </Form.Control>
                                </Col>
                                <Col md={2} className="text-end">
                                    <Button variant="secondary" onClick={() => {
                                        setSearchTerm('');
                                        setStatusFilter('');
                                    }}>Clear Filters</Button>
                                </Col>
                            </Row>
                        </Form>

                        <div className="table-brand-shadow">
                            {renderNewsTable()}
                        </div>
                    </Tab>
                </Tabs>

                <Pagination className="justify-content-end">
                    {[...Array(totalPages)].map((_, i) => (
                        <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => handlePageChange(i + 1)}
                        >
                            {i + 1}
                        </Pagination.Item>
                    ))}
                </Pagination>

                <Modal show={showRejectModal} onHide={closeRejectModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Reject {selectedItemType === 'offer' ? 'Offer' : 'News'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group controlId="rejectionReason">
                                <Form.Label>Reason for Rejection</Form.Label>
                                <Form.Control as="select" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} required>
                                    <option value="">Select reason</option>
                                    <option value="rejected_for_edit">Reject for Edit</option>
                                    <option value="rejected">Permanently Reject</option>
                                </Form.Control>
                            </Form.Group>
                            <Form.Group controlId="comment" className="mt-3">
                                <Form.Label>Rejection Comment</Form.Label>
                                <Form.Control as="textarea" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} required />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeRejectModal}>Cancel</Button>
                        <Button variant="danger" onClick={handleReject}>Reject</Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </Container>
    );
};

export default ApprovalRequestsTable;
